import { InfoCircleOutlined } from '@ant-design/icons'
import { DatePicker } from 'antd'
import moment from 'moment'
import { useMemo } from 'react'
import styled from 'styled-components'
import { FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { FormikType } from 'features/project-form/features/source/components/NewSource'
import {
  extractUrl,
  retrievePureUrl,
} from 'features/project-form/features/source/utils'
import {
  sourceTypes,
  useSourceTypes,
} from 'features/project-form/utils/source-types'

type Props = {
  formik: FormikType
  sourceType: string
}
const dateFormat = 'MM/DD/YYYY'

export const Url: React.FC<Props> = ({ formik, sourceType }) => {
  const { data: catalogSourceTypes } = useSourceTypes()
  const resolvedSourceTypes = catalogSourceTypes ?? sourceTypes
  const sourceUrl = resolvedSourceTypes[sourceType] ?? ''
  const sourceUrlValue = useMemo(
    () => extractUrl(formik.values.sourceUrl, sourceUrl),
    [formik.values.sourceUrl, sourceUrl],
  )

  return (
    <>
      <FormItem
        label={
          <Tooltip content="Provide the URL of the page you want pulled.">
            <span>
              URL <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.sourceUrl ? 'error' : ''}
        help={formik.errors.sourceUrl}
        wrapperCol={{ span: 16 }}
      >
        <Flex>
          {sourceUrl}{' '}
          <Input
            name="sourceUrl"
            onChange={(event) => {
              const domain = retrievePureUrl(sourceUrl)
              const value = retrievePureUrl(event.target.value).replace(
                domain,
                '',
              )

              formik.setFieldValue('sourceUrl', sourceUrl + value)
            }}
            value={sourceUrlValue}
            $error={Boolean(formik.errors.sourceUrl)}
          />
        </Flex>
      </FormItem>

      <FormItem
        label={
          <Tooltip content="Give your source a name to display in the charts. Make sure it's one you'll recognize.">
            <span>
              Source Title <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.sourceTitle ? 'error' : ''}
        help={formik.errors.sourceTitle}
        wrapperCol={{ span: 10 }}
      >
        <Input
          name="sourceTitle"
          onChange={formik.handleChange}
          value={formik.values.sourceTitle}
          $error={Boolean(formik.errors.sourceTitle)}
          placeholder="Insert title here"
        />
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Select the earliest date from which data should be pulled.">
            <span>
              Date <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        wrapperCol={{ span: 16 }}
        validateStatus={formik.errors.sourceDate ? 'error' : ''}
        help={formik.errors.sourceDate}
      >
        <DatePicker
          defaultValue={
            formik.values.sourceDate && moment(formik.values.sourceDate)
          }
          onChange={(value) => {
            if (value) {
              formik.setFieldValue('sourceDate', value.toDate())
            }
          }}
          format={dateFormat}
        />
      </FormItem>
    </>
  )
}

const Flex = styled.div`
  display: flex;
  align-items: center;
`
